import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  BaseProductAttributesProps,
  ChildrenProductAttribute,
  ProductAttribute,
  ProductAttributesPayload,
} from "../types";
import { GET_PRODUCT_ATTRIBUTES } from "../queries";
import { TAXONOMY_CLIENT } from "../../../../utils/consts";

interface GetProductAttributesReturn {
  isLoading: boolean;
  productAttributes: ProductAttribute[];
  baseProductAttributesMap: Record<number, BaseProductAttributesProps>;
  flattenedProductAttributes: (ProductAttribute | ChildrenProductAttribute)[];
}

/**
 * useGetProductAttributes
 *
 * @description - Custom hook to get a list of product attributes
 * @returns {GetProductAttributesReturn} - Returns the product attributes and a boolean indicating if the query is loading
 * @example
 * const { isLoading, productAttributes, baseProductAttributesMap } = useGetProductAttributes();
 */
export const useGetProductAttributes = (): GetProductAttributesReturn => {
  const { data, loading } = useQuery<ProductAttributesPayload>(
    GET_PRODUCT_ATTRIBUTES,
    {
      context: {
        clientName: TAXONOMY_CLIENT,
      },
    }
  );

  const productAttributes = useMemo(
    () => data?.productAttributes?.data ?? [],
    [data?.productAttributes?.data]
  );

  const baseProductAttributesMap = useMemo(() => {
    const map: Record<number, BaseProductAttributesProps> = {};

    for (const productAttribute of productAttributes) {
      map[productAttribute.id] = productAttribute;

      for (const childrenProductAttribute of productAttribute.childrenAttributes)
        map[childrenProductAttribute.id] = childrenProductAttribute;
    }

    return map;
  }, [productAttributes]);

  const flattenedProductAttributes = useMemo(() => {
    const allChildAttributes = productAttributes.flatMap(
      (attribute) => attribute.childrenAttributes
    );
    return [...productAttributes, ...allChildAttributes];
  }, [productAttributes]);

  return {
    isLoading: loading,
    productAttributes,
    baseProductAttributesMap,
    flattenedProductAttributes,
  };
};
